import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { useContext } from "react";
import { AuthContext } from "../context/authContext";
import { useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

export function useCampaigns() {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getCampaigns = () => {
    return axios
      .get(`${baseUrl}/campaigns`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };
  const { isLoading, error, data } = useQuery({
    queryKey: ["all_campaigns", token],
    queryFn: () => getCampaigns(),
    placeholderData: keepPreviousData,
  });

  return { isLoading, error, data: data?.data };
}

export function useSingleCampaign({ id }) {
  const exclusions = [
    "campaigns",
    "calls",
    "contacts",
    "appointments",
    "dashboard",
    "settings",
    "knowledgebase",
  ];
  const isExcluded = exclusions.some((exclusion) => id?.includes(exclusion));
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getCampaigns = () => {
    if (id.trim() && !isExcluded) {
      return axios
        .get(`${baseUrl}/campaigns/${id}`, {
          headers: { Authorization: AuthStr },
        })
        .then((response) => {
          const data = response?.data[0];
          return data;
        })
        .catch((e) => {
          if (e?.response?.data?.message === "Session expired")
            navigate("/account/logout");
          return Promise.reject(
            new Error(e?.message ?? "Check your internet connection")
          );
        });
    } else return new Error();
  };
  const { isLoading, error, data, refetch } = useQuery({
    queryKey: [!isExcluded && id, token],
    queryFn: () => (id ? getCampaigns() : null),
    placeholderData: keepPreviousData,
  });

  return { isLoading, error, data, refetch };
}

export function useSingleCampaignCall({ id }) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getCampaigns = () => {
    return axios
      .get(`${baseUrl}/calls?campaign_id=${id}`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };
  const { isLoading, error, data } = useQuery({
    queryKey: [`${id}_campaign_calls`, token],
    queryFn: () => getCampaigns(),
    placeholderData: keepPreviousData,
  });

  return { isLoading, error, data };
}

export function useSingleAvailability({ id }) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getCampaigns = () => {
    return axios
      .get(`${baseUrl}/appointments/get_campaign_availability/${id}`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };
  const { isLoading, error, data } = useQuery({
    queryKey: [`${id}_campaign_availability`, token],
    queryFn: () => getCampaigns(),
    placeholderData: keepPreviousData,
  });

  return { isLoading, error, data };
}

export function useSingleCampaignLead({ id }) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getCampaigns = () => {
    return axios
      .get(`${baseUrl}/leads?campaign_id=${id}`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };
  const { isLoading, error, data } = useQuery({
    queryKey: ["leads", id, token],
    queryFn: () => getCampaigns(),
    placeholderData: keepPreviousData,
  });

  return { isLoading, error, data };
}

export function useCampaignAnalytics({ id }) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getCampaigns = () => {
    return axios
      .get(`${baseUrl}/campaigns/get_campaign_analytics/${id}`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };
  const { isLoading, error, data } = useQuery({
    queryKey: [id, "campaign_analytics", token],
    queryFn: () => getCampaigns(),
    placeholderData: keepPreviousData,
  });

  return { isLoading, error, data };
}

export function useTestCampaignVariables({ id }) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();

  const getTestCampaignVariables = () => {
    return axios
      .get(`${baseUrl}/campaigns/${id}/get_test_call_parameters`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response?.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };
  const { isLoading, error, data, refetch } = useQuery({
    queryKey: [`${id}_test`, token],
    queryFn: () => (id ? getTestCampaignVariables() : null),
    placeholderData: keepPreviousData,
    refetchInterval: false,
    enabled: !!id,
  });

  return { isLoading, error, data, refetch };
}
